import { Link, useParams } from "react-router-dom";
import { ArrowUpRight, Check, ArrowRight, BadgePercent } from "lucide-react";
import { SERVICE_PAGES } from "../data/content";
import { Reveal, useSeo } from "../lib/motion";
import { CTASection, Breadcrumb } from "../components/CTASection";

export default function ServicePage({ pageKey }) {
  const params = useParams();
  const key = pageKey || params.key;
  const p = SERVICE_PAGES[key];
  useSeo(p?.seoTitle, p?.seoDesc);
  if (!p) return <div className="pt-40 pb-40 text-center">Page introuvable</div>;

  return (
    <>
      <section className="relative bg-ink text-white overflow-hidden">
        <div className="absolute inset-0">
          <img src={p.image} alt={p.h1} className="w-full h-full object-cover opacity-25" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/85 to-ink/60" />
          <div className="absolute inset-0 tech-grid opacity-20" />
        </div>
        <div className="relative max-w-7xl mx-auto px-6 md:px-12 pt-40 pb-24">
          <div className="[&_*]:text-white/60">
            <Breadcrumb items={p.breadcrumb} />
          </div>
          <span className="inline-block text-xs font-bold uppercase tracking-[0.25em] text-icelight mt-8">{p.label}</span>
          <h1 className="font-heading font-black tracking-tight text-4xl md:text-6xl mt-5 max-w-4xl leading-[0.98]">{p.h1}</h1>
          <p className="text-white/70 text-lg md:text-xl mt-7 max-w-2xl">{p.intro}</p>
          <Link to="/demande-de-devis" className="inline-flex items-center gap-2 bg-ice text-white rounded-md px-8 py-4 font-bold mt-10 hover:bg-icehover transition-transform hover:scale-105">
            {p.cta} <ArrowUpRight size={18} />
          </Link>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24 md:py-28">
        <Reveal className="mb-14 max-w-2xl">
          <h2 className="font-heading font-black tracking-tight text-3xl md:text-4xl text-ink leading-[1.05]">
            {key === "climatisation" ? "Quelle solution choisir ?" : "Nos prestations"}
          </h2>
        </Reveal>
        <div className="grid md:grid-cols-2 gap-6">
          {p.solutions.map((s, i) => (
            <Reveal key={s.title} delay={i * 0.07}>
              <div className="bg-white border border-line rounded-md p-8 h-full hover:border-ice/40 hover:shadow-lg transition-[box-shadow,border-color] duration-300">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-heading font-bold text-xl md:text-2xl text-ink">{s.title}</h3>
                  <span className="font-mono text-sm text-ice">0{i + 1}</span>
                </div>
                <p className="text-maintext mt-3 leading-relaxed">{s.text}</p>
                {s.to && <Link to={s.to} className="inline-flex items-center gap-1.5 text-ice font-bold text-sm mt-5 hover:gap-3 transition-[gap]">En savoir plus <ArrowRight size={15} /></Link>}
              </div>
            </Reveal>
          ))}
        </div>

        {p.blocks && (
          <div className="grid md:grid-cols-2 gap-6 mt-6">
            {p.blocks.map((b) => (
              <Reveal key={b.title}>
                <div className="bg-ink text-white rounded-md p-9 h-full">
                  <h3 className="font-heading font-black text-2xl md:text-3xl">{b.title}</h3>
                  <p className="text-white/60 mt-4 leading-relaxed">{b.text}</p>
                </div>
              </Reveal>
            ))}
          </div>
        )}

        {p.points && (
          <Reveal className="flex flex-wrap gap-3 mt-12">
            {p.points.map((pt) => (
              <span key={pt} className="inline-flex items-center gap-2 bg-cloud border border-line rounded-md px-4 py-2.5 font-semibold text-ink text-sm"><Check size={16} className="text-ice" /> {pt}</span>
            ))}
          </Reveal>
        )}

        {p.steps && (
          <Reveal className="mt-14 flex flex-wrap gap-x-8 gap-y-4">
            {p.steps.map((st, i) => (
              <span key={st} className="font-heading font-bold text-lg text-ink flex items-center gap-3">
                <span className="font-mono text-ice text-sm">0{i + 1}</span> {st}
              </span>
            ))}
          </Reveal>
        )}

        {p.note && (
          <Reveal className="mt-12 border-l-4 border-ice bg-cloud rounded-r-md p-6 max-w-3xl">
            <p className="text-maintext leading-relaxed"><strong className="text-ink">À noter :</strong> {p.note}</p>
          </Reveal>
        )}
      </section>

      {p.aides && (
        <section className="max-w-7xl mx-auto px-6 md:px-12 pb-8 md:pb-12">
          <Reveal>
            <div className="rounded-md border border-line bg-ink text-white overflow-hidden">
              <div className="p-8 md:p-10 relative">
                <div className="absolute inset-0 tech-grid opacity-20" aria-hidden />
                <div className="relative max-w-3xl">
                  <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.25em] text-icelight"><BadgePercent size={15} /> Financement</span>
                  <h2 className="font-heading font-black tracking-tight text-2xl md:text-3xl mt-4">Aides &amp; TVA réduite</h2>
                  <p className="text-white/70 mt-4 leading-relaxed">Selon votre logement, l’équipement choisi et ses performances, votre installation peut être éligible à :</p>
                  <ul className="mt-5 space-y-3">
                    {[
                      "TVA à 5,5 % sur certaines pompes à chaleur air/air répondant aux critères réglementaires ;",
                      "TVA à 10 % sur certaines prestations réalisées dans les logements achevés depuis plus de 2 ans ;",
                      "Prime CEE pour certaines pompes à chaleur air/air éligibles.",
                    ].map((line) => (
                      <li key={line} className="flex items-start gap-3 text-white/85 leading-relaxed"><Check size={18} className="text-icelight mt-1 shrink-0" /> {line}</li>
                    ))}
                  </ul>
                  <p className="text-white/70 mt-5 leading-relaxed">L’éligibilité est vérifiée avant établissement du devis.</p>
                  <p className="text-white/45 text-sm mt-6 border-t border-white/10 pt-5">Les dispositifs et conditions peuvent évoluer. Le taux applicable et les aides éventuelles sont vérifiés selon le matériel et la situation du logement.</p>
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      <CTASection />
    </>
  );
}
